import React, { useMemo } from 'react';

import {
  analyzeBriefCompleteness,
  briefElementLabels,
  isBriefReadyForGeneration,
} from '../../lib/analyzeBriefCompleteness';
import { cn } from '../../../lib/cn';

const WIZARD_LABEL =
  'mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500';
const WIZARD_INPUT =
  'w-full rounded-lg border border-transparent bg-gray-50 px-3 py-2.5 text-sm text-gray-900 transition placeholder:text-gray-400 focus:border-transparent focus:bg-white focus:outline-none focus:ring-2 focus:ring-gray-900';

interface BriefEditorPanelProps {
  value: string;
  onChange: (value: string) => void;
  isLoading?: boolean;
  helperText?: string;
}

export function BriefEditorPanel({
  value,
  onChange,
  isLoading = false,
  helperText,
}: BriefEditorPanelProps) {
  const analysis = useMemo(() => analyzeBriefCompleteness(value), [value]);
  const labels = briefElementLabels();
  const ready = isBriefReadyForGeneration(analysis);

  return (
    <div className="space-y-4">
      {helperText ? (
        <p className="text-xs leading-relaxed text-gray-500">{helperText}</p>
      ) : null}

      <label className="block">
        <span className={WIZARD_LABEL}>Brief técnico de la unidad</span>
        <textarea
          className={cn(WIZARD_INPUT, 'min-h-[180px] resize-y', isLoading && 'opacity-60')}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={isLoading}
          placeholder={[
            '[OBJETIVO] Qué debe lograr el curso al cerrar la ruta.',
            '[CONTENIDO] OA, conceptos y habilidades clave.',
            '[METODOLOGÍA] Secuencia cognitiva entre hitos.',
            '[CONTEXTO] Nivel, recursos y restricciones del aula.',
            '[PRODUCTO] Evidencia observable de logro.',
          ].join('\n')}
        />
      </label>

      <div>
        <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
          <div
            className={cn(
              'h-full rounded-full transition-all duration-300',
              ready ? 'bg-emerald-500' : analysis.completitud >= 40 ? 'bg-amber-400' : 'bg-gray-300',
            )}
            style={{ width: `${analysis.completitud}%` }}
          />
        </div>
        <p className="mt-2 text-[11px] text-gray-500">
          {analysis.elementosDetectados.length}/5 elementos detectados
          {ready ? ' · Listo para generar' : ' · Ajusta el brief hasta completar el esqueleto'}
        </p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {Object.entries(labels).map(([key, label]) => {
            const active = analysis.elementosDetectados.includes(key);
            return (
              <span
                key={key}
                className={cn(
                  'rounded-full px-2 py-0.5 text-[10px] font-semibold',
                  active ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-400',
                )}
              >
                {label}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export { isBriefReadyForGeneration };
